import React, { createRef, MutableRefObject, useContext, useMemo, useState } from 'react';
import { ButtonLoader, ResendOtp } from '..';
import { Constants, Strings } from '../../constants';
import { getDisplayErrorMsg, isMobile, setCookie } from '../../utils';
import { AccountContext, AccountContextInterface } from '../../contexts';
import { ScreenState } from '../../types';
import AccountPhoneInputField from '../phone-input';
import { Account } from '../../models';

type Props = {
  account: Account | null | undefined;
  setAccount: (account: Account) => void;
  createRecco: () => void;
  displayError: string;
  setDisplayError: (message: string) => void;
  state: ScreenState;
  setState: (newState: ScreenState) => void;
};

export const VerifyUser: React.FC<Props> = ({
  state,
  setState,
  account,
  setAccount,
  displayError,
  setDisplayError,
  createRecco,
}) => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const [otpSent, setOtpSent] = useState<boolean>(false);
  const [resendOtpCounter, setResendOtpCounter] = useState<number>(1);
  const [invalidPhoneNumberMsg, setInvalidPhoneNumberMsg] = useState<string>('');
  const [submitPhoneLoader, setSubmitPhoneLoader] = useState<boolean>(false);
  const [submitOtpLoader, setSubmitOtpLoader] = useState<boolean>(false);
  const [verifyEnabled, setVerifyEnabled] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumebr] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('');
  const [OTP, setOTP] = useState<string[]>(new Array(5).fill(''));
  const [name, setName] = useState<string>('');
  const [isExistingUser, setIsExistingUser] = useState(false);
  const otp = (): string => OTP.join('');

  const otpTextInputRefs: MutableRefObject<HTMLInputElement | null>[] = useMemo(() => {
    const refs: MutableRefObject<HTMLInputElement | null>[] = [];
    for (let i = 0; i < OTP.length; i++) {
      refs[i] = createRef<HTMLInputElement>();
    }
    return refs;
  }, [OTP.length]);

  const handlePhoneInput = (value: any, data: any) => {
    setPhoneNumebr(value);
    setCountryCode(data.dialCode);
    setCookie('token', '', 1);
  };

  const sendOtp = async (account: Account): Promise<void> => {
    const { data, error } = await accountService.sendOtp(account.id);
    if (data && !error) {
      setState(ScreenState.SUCCESS_WITH_NO_DATA);
    } else {
      setDisplayError(getDisplayErrorMsg(error));
      setState(ScreenState.ERROR);
    }
  };

  const handleSubmitPhone = async (): Promise<void> => {
    if (phoneNumber === '' || phoneNumber === null || phoneNumber === undefined) {
      return;
    } else if (!new RegExp(/^[\s()+-]*([0-9][\s()+-]*){10,15}$/).test(phoneNumber)) {
      setInvalidPhoneNumberMsg('Please specify a valid mobile number with country code.');
      setState(ScreenState.ERROR);
      return;
    }
    setSubmitPhoneLoader(true);

    const { data: results, error: searchError } = await accountService.search([{
      phoneNumber: phoneNumber.replace(countryCode, ''),
      countryCode: `+${countryCode}`,
    }]);

    if (searchError) {
      setDisplayError(getDisplayErrorMsg(searchError));
      setState(ScreenState.ERROR);
      return;
    }

    if (results?.[0]?.account?.id) {
      setAccount(results?.[0]?.account);
      results?.[0]?.account.firstName && setName(results?.[0]?.account.displayName);
      results?.[0]?.account.firstName && setIsExistingUser(true);
      await sendOtp(results?.[0]?.account);
      setSubmitPhoneLoader(false);
      setOtpSent(true);
    } else {
      const { data: account, error: createError } = await accountService.create({
        phoneNumber: phoneNumber.replace(countryCode, ''),
        countryCode: `+${countryCode}`,
      });
      if (createError) {
        setDisplayError(getDisplayErrorMsg(createError));
        setState(ScreenState.ERROR);
        setSubmitPhoneLoader(false);
        return;
      }
      if (account?.id) {
        setAccount(account);
        await sendOtp(account);
        setSubmitPhoneLoader(false);
        setOtpSent(true);
      }
    }
  };

  const resendOtp = async (): Promise<void> => {
    setState(ScreenState.DEFAULT);
    const { data, error } = await accountService.sendOtp(account!!.id);
    if (data && !error) {
      setResendOtpCounter(r => r + 1);
      setOTP(OTP.fill(''));
      (otpTextInputRefs[0].current as any).focus();
      setState(ScreenState.DEFAULT);
    } else {
      if (error?.toLowerCase().includes('maximum')) {
        setResendOtpCounter(5);
        return;
      }
      setDisplayError(getDisplayErrorMsg(error));
      setState(ScreenState.ERROR);
    }
  };

  const focusPreviousInput = (e: React.KeyboardEvent<HTMLInputElement>, index: number): void => {
    if (e.nativeEvent.key === 'Backspace' && index && OTP[index] === '') {
      (otpTextInputRefs[index - 1].current as any).focus();
      OTP[index - 1] = '';
      setOTP([...OTP]);
    }
  };

  const onOtpChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setDisplayError('');
    const code = /(\d{5})/g.exec(e.target.value)?.[1] || '';
    if (isNaN(+e.target.value)) {
      if (!code) {
        return;
      }
    }

    if (index === 0 && code.length === 5) {
      setOTP(code.split(''));
      (otpTextInputRefs[index].current as any).blur();
      return;
    }

    OTP[index] = e.target.value;
    setOTP([...OTP]);

    const notBlank = OTP[index] !== '';
    const lastDigit = index === otpTextInputRefs.length - 1;
    lastDigit && notBlank && (otpTextInputRefs[index].current as any).blur();
    otpTextInputRefs[index + 1] && notBlank && (otpTextInputRefs[index + 1].current as any).focus();
    checkVerifyEnabled();
  };

  const handleNameChange = (value: string) => {
    setName(value);
    checkVerifyEnabled();
  };

  const checkVerifyEnabled = () => {
    if (!OTP?.filter((otp) => otp === '').length && name.length > 1) {
      setVerifyEnabled(true);
    } else {
      setVerifyEnabled(false);
    }
  };

  const handleVerify = async (): Promise<void> => {
    const code = otp();
    if (code.length !== 5) {
      return;
    }
    setSubmitOtpLoader(true);

    const { data: auth, error } = await accountService.verifyOtp(account!!.id, code);
    if (auth && !error) {
      setState(ScreenState.SUCCESS_WITH_DATA);
      setSubmitOtpLoader(false);
      if (auth.token) {
        setCookie('token', auth.token, 365);
      }
      !isExistingUser && accountService.updateAccount(account!!.id, {
        firstName: name,
      });
      setVerifyEnabled(false);
      createRecco();
    } else {
      setDisplayError(getDisplayErrorMsg(error));
      setVerifyEnabled(false);
      setState(ScreenState.ERROR);
      setSubmitOtpLoader(false);
      setOTP(new Array(Constants.OTP_CODE_LENGTH).fill(''));
      (otpTextInputRefs[0].current as any).focus();
    }
  };

  const hasError = state === ScreenState.ERROR;

  return (
    <div className="max-w-lg mx-auto p-4">
      <p className="text-xl font-semibold text-center mb-5">{Strings.OTP_VERIFY_HEADING}</p>

      <label className="block mb-2 font-semibold">{Strings.NAME_LABEL}</label>
      <input
        onChange={(e) => handleNameChange(e.target.value)}
        placeholder={Strings.NAME_PLACEHOLDER}
        value={name}
        className="w-full p-2 border rounded-md mb-4"
      />

      <label className="block mb-2 font-semibold">{Strings.PHONE_LABEL}</label>
      <div className="flex-col mb-4 w-full">
        <AccountPhoneInputField
          errorClassName="account-field-icon"
          placeholder="xxxxxxxxxxxx"
          label="Phone"
          name="phone"
          value={phoneNumber}
          invalid={false}
          invalidMsg={invalidPhoneNumberMsg}
          disabled={submitPhoneLoader}
          handleInputChange={handlePhoneInput}
        />
        <button
          className={`btn btn-primary fw-bold mt-3 sm:mt-0 w-full sm:w-auto ${otpSent ? 'hidden' : ''}`}
          disabled={submitPhoneLoader}
          onClick={handleSubmitPhone}
        >
          {submitPhoneLoader ? <ButtonLoader /> : 'Send OTP'}
        </button>
      </div>

      <div className={`flex flex-col ${!otpSent ? 'hidden' : ''}`}>
        <div className="flex mb-3">
          <div className="flex w-full justify-between">
            {OTP.map((digit, index) => (
              <input
                key={index}
                ref={(ref) => (otpTextInputRefs[index].current = ref)}
                onChange={(e) => onOtpChange(e, index)}
                onKeyUp={(e) => focusPreviousInput(e, index)}
                placeholder="-"
                value={OTP[index]}
                autoFocus={index === 0}
                className="w-1/5 p-2 border rounded-md mx-1 text-center"
              />
            ))}
          </div>
        </div>
        {resendOtpCounter <= 5 ? (
          otpSent && <ResendOtp onClick={resendOtp} />
        ) : (
          <div>OTP limit reached, Please try again later</div>
        )}
      </div>

      {hasError && (
        <span className="text-red-500 block text-center mt-3">{displayError}</span>
      )}

      <button
        className={`w-full sm:w-1/2 py-2 rounded-full font-semibold text-white mt-5 ${verifyEnabled ? 'bg-blue-600' : 'bg-gray-400'
          } ${submitOtpLoader ? 'cursor-wait' : 'cursor-pointer'}`}
        disabled={!verifyEnabled || submitOtpLoader}
        onClick={handleVerify}
      >
        {submitOtpLoader ? <ButtonLoader /> : Strings.VERIFY}
      </button>
    </div>
  );
};
